/**
 * @file useTemplateReset.ts
 * @description «Сбросить к стандартному» и применение раскладки страницы
 * товара (phase19.16 Р2а; uiux-design-r2a §3, §6; db-design-r2a, ответ 4;
 * контракт §19 `POST /admin/site/templates/product/reset { layout? }`).
 *
 * Оба действия заменяют все блоки шаблона в черновике, поэтому спрашивают
 * подтверждение; на сайте прежний шаблон — до «Опубликовать». Если в
 * шаблоне были неопубликованные правки, окно предупреждает, что заменятся и
 * они. Отменить замену нельзя (TD-351) — всплывашка этого и не обещает.
 * История «Отменить / Вернуть» блоков после замены очищается (блоки новые).
 */

import type { ProductTemplateLayoutKey } from "@chaiberi/shared";
import { useCallback } from "react";

import { useBuilder } from "../../components/site-builder/builder-context";
import { useSiteView } from "../../components/site-view/site-view-context";
import { useNotify } from "../../components/ui/NotifyProvider";
import { siteTemplates } from "../../lib/api-site-templates";
import { blockErrorText } from "../../lib/site-builder/block-errors";

/** Тексты окон и всплывашек (uiux-design-r2a §6). */
export const RESET_TEXT = {
  resetTitle: "Сбросить шаблон к стандартному?",
  layoutTitle: (title: string) => `Применить раскладку «${title}»?`,
  body: "Все блоки шаблона в черновике заменятся. На сайте прежний шаблон — до «Опубликовать».",
  bodyWithEdits: "Все блоки шаблона в черновике заменятся — вместе с неопубликованными правками шаблона. На сайте прежний шаблон — до «Опубликовать».",
  resetDone: "Шаблон вернули к стандартному в черновике. На сайте прежний — до «Опубликовать»",
  layoutDone: (title: string) => `Применена раскладка «${title}» в черновике. На сайте прежний шаблон — до «Опубликовать»`,
} as const;

export interface TemplateResetApi {
  /** Без раскладки — стандартный шаблон; `true` — заменено. */
  reset: (layout?: { key: ProductTemplateLayoutKey; title: string }) => Promise<boolean>;
}

export function useTemplateReset(): TemplateResetApi {
  const b = useBuilder();
  const view = useSiteView();
  const notify = useNotify();
  const { pages, undo, confirm } = b;
  const { changes } = view;

  const reset = useCallback(
    async (layout?: { key: ProductTemplateLayoutKey; title: string }) => {
      // Неопубликованные правки шаблона: флаг части из ответа шаблона мог устареть (перестановка
      // блоков шаблон не перечитывает), поэтому — «или» со свежим «что изменено».
      const hadEdits = (pages.template?.part?.changed ?? false) || (changes?.changedParts.some((p) => p.part === "PRODUCT_TEMPLATE") ?? false);
      const ok = await confirm({
        title: layout ? RESET_TEXT.layoutTitle(layout.title) : RESET_TEXT.resetTitle,
        description: hadEdits ? RESET_TEXT.bodyWithEdits : RESET_TEXT.body,
        action: layout ? "Применить раскладку" : "Сбросить",
        danger: true,
      });
      if (!ok) return false;
      try {
        pages.applyTemplate(await siteTemplates.reset(layout?.key));
      } catch (err) {
        notify.error(blockErrorText(err, "Не удалось заменить шаблон — черновик прежний."));
        return false;
      }
      undo.clear();
      pages.bumpCanvas();
      void b.select({ sel: { kind: "page" } });
      const text = layout ? RESET_TEXT.layoutDone(layout.title) : RESET_TEXT.resetDone;
      notify.success(text);
      return true;
    },
    [b, changes, confirm, notify, pages, undo],
  );

  return { reset };
}
